/**
 * Copyright 2025-2026 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { useState, useMemo, useEffect } from "react";
import { RowSelectionState } from "@tanstack/table-core";
import { Else, If, Then } from "react-if";
import type { Report } from "~/generated/graphql";
import {
  ReportsSortFields,
  SortOrder,
  ReportStatus,
} from "~/generated/graphql";
import { LoaderWrap, NoData } from "~/components";
import { Pagination } from "~/components/Pagination";
import {
  getNoSearchResultsMessage,
  minSearchLength,
} from "~/components/SearchInput";
import { TableWrapper } from "~/components/Tables";
import { useQueryState } from "~/hooks";
import { isEmpty, isNotNil } from "~/tools";
import { useGetReports } from "~/views/resources/hooks";
import { ReportsQuery } from "~/views/resources/types";
import { prepareReportsFilter } from "./reportFilterBuilders";
import { columns } from "./ReportsColumns";
import { ReportsListHeader } from "./ReportsListHeader";

const POLLING_INTERVAL = 500;

export function ReportsTable() {
  const { queryState, removeQueryState } = useQueryState<ReportsQuery>();
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [highlightedReportId, setHighlightedReportId] = useState<string>();

  const filter = useMemo(() => prepareReportsFilter(queryState), [queryState]);

  const { loading, data, count, startPolling, stopPolling } = useGetReports({
    page: queryState.page,
    limit: queryState.limit,
    sort: {
      field: queryState.field ?? ReportsSortFields.DateGenerated,
      order: queryState.order ?? SortOrder.Desc,
    },
    filter,
  });

  useEffect(() => {
    if (!queryState.newReportId) {
      return;
    }
    setHighlightedReportId(queryState.newReportId);
    removeQueryState(["newReportId"], "replace");
  }, [queryState.newReportId, removeQueryState]);

  const reports = useMemo(() => data.filter(isNotNil) ?? [], [data]);

  useEffect(() => {
    if (!highlightedReportId) {
      stopPolling();
      return;
    }

    const highlightedReport = reports.find(
      (report) => report.id === highlightedReportId
    );

    if (!highlightedReport) {
      stopPolling();
      return;
    }

    if (highlightedReport.status === ReportStatus.InProgress) {
      startPolling(POLLING_INTERVAL);
    } else {
      stopPolling();
    }

    return () => stopPolling();
  }, [highlightedReportId, reports, startPolling, stopPolling]);

  const isSearchAllowed =
    !queryState.searchPhrase ||
    queryState.searchPhrase.length >= minSearchLength;

  return (
    <>
      <ReportsListHeader resultsCount={count ?? 0} />
      <If condition={!isSearchAllowed}>
        <Then>
          <NoData hasIcon message={getNoSearchResultsMessage()} />
        </Then>
        <Else>
          <LoaderWrap loading={loading} inContent>
            <If condition={isEmpty(reports)}>
              <Then>
                <NoData hasIcon message={"There are no Reports."} />
              </Then>
              <Else>
                <TableWrapper<Report>
                  loading={loading}
                  data={reports}
                  columns={columns({
                    highlightedReportId,
                    setHighlightedReportId,
                  })}
                  rowSelection={rowSelection}
                  setRowSelection={setRowSelection}
                  stripped
                />
                <Pagination count={count} />
              </Else>
            </If>
          </LoaderWrap>
        </Else>
      </If>
    </>
  );
}
